import PropTypes from 'prop-types';
import styled from 'styled-components';
import { themeGet } from '@styled-system/theme-get';
import { BLOCKS } from '@contentful/rich-text-types';

import SectionGraphic from './SectionGraphic';
import SectionContainer from './SectionContainer';
import RichText from './RichTextV2';

const Section = styled(SectionGraphic)`
  padding: 20px 0 40px;

  @media only screen and (min-width: ${themeGet('breakpoints.lg')}) {
    padding: 26px 0 80px;
  }
`;

const Heading = styled.h2`
  font-family: ${themeGet('typography.mobile.heading1.fontFamily')};
  font-weight: ${themeGet('typography.mobile.heading2.fontWeight')};
  font-size: ${themeGet('typography.mobile.heading2.fontSize')};
  line-height: ${themeGet('typography.mobile.heading2.lineHeight')};
  letter-spacing: ${themeGet('typography.mobile.heading2.letterSpacing')};
  text-align: center;
  margin: 0 0 30px 0;

  @media only screen and (min-width: ${themeGet('breakpoints.lg')}) {
    font-size: ${themeGet('typography.desktop.heading2.fontSize')};
    line-height: ${themeGet('typography.desktop.heading2.lineHeight')};
    margin: 0 0 60px 0;
  }
`;

const StepsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;

  @media only screen and (min-width: ${themeGet('breakpoints.md')}) {
    flex-direction: row;
    align-items: stretch;
    justify-content: space-around;

    @supports (gap: 10%) {
      gap: min(10vw, 100px);
      justify-content: center;
    }
  }
`;

const SignUpStep = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 155px;
  margin: 0 0 30px 0;

  &:last-child {
    margin: 0;
  }

  @media only screen and (min-width: ${themeGet('breakpoints.md')}) {
    margin: 0;
  }

  @media only screen and (min-width: ${themeGet('breakpoints.lg')}) {
    max-width: 200px;
  }
`;

const StepIcon = styled.div`
  background-color: ${themeGet('colors.velvet')};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 18px;
  border-radius: 20px;

  @media only screen and (min-width: ${themeGet('breakpoints.lg')}) {
    padding: 15px;
  }
`;

const StepNumber = styled.div`
  background-color: ${themeGet('colors.red')};
  color: ${themeGet('colors.white')};
  border-radius: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 26px;
  width: 26px;
  margin: 10px 0;

  p {
    margin: 0;
    font-family: ${themeGet('typography.mobile.heading1.fontFamily')};
    font-weight: ${themeGet('typography.mobile.heading5.fontWeight')};
    font-size: ${themeGet('typography.mobile.heading5.fontSize')};
    line-height: ${themeGet('typography.mobile.heading5.lineHeight')};
    letter-spacing: ${themeGet('typography.mobile.heading5.letterSpacing')};
  }

  @media only screen and (min-width: ${themeGet('breakpoints.lg')}) {
    height: 46px;
    width: 46px;
    margin: 22px 0;
  }
`;

const Text = styled.p`
  font-family: ${themeGet('typography.mobile.heading1.fontFamily')};
  font-weight: ${themeGet('typography.mobile.body3.fontWeight')};
  font-size: ${themeGet('typography.mobile.body3.fontSize')};
  line-height: ${themeGet('typography.mobile.body3.lineHeight')};
  letter-spacing: ${themeGet('typography.mobile.body3.letterSpacing')};
  text-align: center;
  margin: 0;

  @media only screen and (min-width: ${themeGet('breakpoints.lg')}) {
    font-size: ${themeGet('typography.desktop.body3.fontSize')};
    line-height: ${themeGet('typography.desktop.body3.lineHeight')};
  }
`;

const SignUpStepOptions = {
  renderNode: {
    [BLOCKS.HEADING_1]: (_, children) => <Heading>{children}</Heading>,
    [BLOCKS.HEADING_2]: (_, children) => <Heading>{children}</Heading>,
    [BLOCKS.PARAGRAPH]: (_, children) => <Text>{children}</Text>,
  },
};

const SignUpSteps = ({ data }) => {
  if (!data) return null;

  const {
    sectionId,
    backgroundColor,
    imageDamAsset,
    sectionTitle,
    combinatorSectionItems,
  } = data;

  return (
    <Section
      id={sectionId}
      background={backgroundColor || undefined}
      img={imageDamAsset}
    >
      <SectionContainer>
        <RichText document={sectionTitle} options={SignUpStepOptions} />
        <StepsWrapper>
          {combinatorSectionItems.map(
            ({ id, iconImageDam, paragraphRTF }, index) => (
              <SignUpStep key={id}>
                <StepIcon>
                  <img
                    src={iconImageDam.asset.localImage.publicURL}
                    alt={iconImageDam.altText}
                    loading="lazy"
                  />
                </StepIcon>
                <StepNumber>
                  <p>{index + 1}</p>
                </StepNumber>
                <RichText
                  document={paragraphRTF}
                  options={SignUpStepOptions}
                />
              </SignUpStep>
            ),
          )}
        </StepsWrapper>
      </SectionContainer>
    </Section>
  );
};

SignUpSteps.propTypes = {
  data: PropTypes.shape().isRequired,
};

export default SignUpSteps;
